/**
 * Breadcrumb Middleware
 * 
 * A Redux middleware that silently monitors the global Redux event stream. 
 * Whenever it sees specific authentication actions fire (like "User Logged In" 
 * or "Session Expired"), it automatically grabs them and pushes an `auth` breadcrumb 
 * to the BreadcrumbService without requiring manual developer intervention.
 */
import { Middleware } from '@reduxjs/toolkit';
import { BreadcrumbService } from '../util/telemetry/breadcrumb/breadcrumb.service';

const AUTH_ACTIONS: Record<string, string> = {
    'commonSlice/updateLogin': 'User Logged In',
    'commonSlice/updateLogout': 'User Logged Out',
    'commonSlice/updateIsSessionExpired': 'Session Expired',
    'commonSlice/updateIsRefreshTokenExpired': 'Refresh Token Expired',
    'kioskAuthSlice/setKioskSession': 'Kiosk User Logged In',
    'kioskAuthSlice/clearKioskSession': 'Kiosk User Logged Out'
};

export const breadcrumbMiddleware: Middleware = () => (next) => (action: unknown) => {
    // Safely check if action is a standard Redux action object
    if (action && typeof action === 'object' && 'type' in action) {
        const typedAction = action as { type: string; payload?: unknown };
        
        if (AUTH_ACTIONS[typedAction.type]) {
            let shouldLog = true;

            // For boolean toggles, only log when they become true to avoid noise
            if (
                typedAction.type === 'commonSlice/updateIsSessionExpired' || 
                typedAction.type === 'commonSlice/updateIsRefreshTokenExpired'
            ) {
                shouldLog = typedAction.payload === true;
            }

            if (shouldLog) {
                BreadcrumbService.add({
                    category: 'auth',
                    message: AUTH_ACTIONS[typedAction.type],
                    severity: typedAction.type.includes('Expired') ? 'warning' : 'info'
                });
            }
        }
    }
    
    return next(action);
};
